/**
 * CardTableBridge
 *
 * Synchronizes boardgame.io game state (G) with cards.js visual layer.
 *
 * Flow:
 * 1. React Board receives new G state via props
 * 2. Board calls bridge.sync(G) in useEffect
 * 3. Bridge compares G with current cards.js state
 * 4. Bridge moves/flips cards.js objects to match G
 *
 * Inverse flow (user interaction):
 * 1. cards.js click handler fires
 * 2. Handler calls callback with card identifier
 * 3. Board component calls moves.playCard(cardId)
 * 4. boardgame.io updates G, triggers re-render
 * 5. Sync cycle begins again
 *
 * Container types per player:
 * - stack_{id}: Face-down draw pile
 * - hand_{id}: Player's hand (face-up for owner only)
 * - general_{id}: Single General card (face-up for all)
 * - defender1_{id}: First defender card (face-up, stackable for attacks)
 * - defender2_{id}: Second defender card (face-up, stackable for attacks)
 * - stash_{id}: Single stashed card (face-down for all)
 */

import { cards, $ } from '../cards/setup.js';

// Physical seat assignments per player count (clockwise: 0=bottom, 1=left, 2=top, 3=right)
const PHYSICAL_SEATS = {
  2: [0, 2],       // Players at bottom and top (opposite sides)
  3: [0, 1, 2],    // Players at bottom, left, top (right empty)
  4: [0, 1, 2, 3], // Players at all four positions
};

// Maps clockwise table position to edgePositions array index
// edgePositions uses: 0=bottom, 1=top, 2=left, 3=right
// clockwise order is: 0=bottom, 1=left, 2=top, 3=right
const CLOCKWISE_TO_EDGE = [0, 2, 1, 3];

class CardTableBridge {
  constructor(options = {}) {
    this.tableSelector = options.tableSelector || '#card-table';
    this.onCardClick = options.onCardClick || (() => {});
    this.containers = {};
    this.combatInfo = {}; // containerName → { baseCard: string, attacks: string[] }
    this._currentPlayerIdx = 0; // For calculating relative player positions
    this.initialized = false;
    this.cardsInitialized = false;
  }

  /**
   * Initialize cards.js with the table element.
   * Filters to Bavarian 36-card deck (ranks 6-K and Ace).
   * Call once when Board mounts.
   */
  init(options = {}) {
    if (this.initialized) return;

    // Force reset cards.all before init - cards.js uses global state,
    // so we must clear it when reinitializing for a different player view
    for (let i = 0; i < cards.all.length; i++) {
      if (cards.all[i].el) {
        $(cards.all[i].el).remove();
      }
    }
    cards.all.length = 0;

    const cardsUrl = options.cardsUrl || '../cards-js/img/cards.png';

    cards.init({
      table: this.tableSelector,
      type: window.STANDARD,
      cardsUrl,
      animationSpeed: 300,
    });

    // Filter to Bavarian 36-card deck (ranks 6-13 and Ace)
    const bavarianCards = cards.all.filter((card) => {
      const rank = card.rank;
      return rank >= 6 || rank === 1; // 6-13 (6-K) or 1 (Ace)
    });

    // Remove non-Bavarian cards from DOM
    for (const card of cards.all) {
      if (!bavarianCards.includes(card)) {
        $(card.el).remove();
      }
    }

    // Replace cards.all with filtered array
    cards.all.length = 0;
    cards.all.push(...bavarianCards);

    this.initialized = true;
  }

  /**
   * Get the table position (view position) for a player from the viewer's perspective.
   * Uses physical seat assignments to ensure consistent clockwise positioning.
   * @param {number} playerIdx - The player to position
   * @param {number} viewerIdx - The viewer's player index
   * @param {number} numPlayers - Total number of players (2, 3, or 4)
   * @returns {number} Table position: 0=bottom, 1=left, 2=top, 3=right
   */
  _getViewPosition(playerIdx, viewerIdx, numPlayers) {
    const seats = PHYSICAL_SEATS[numPlayers];
    const physicalPos = seats[playerIdx];
    const viewerPhysicalPos = seats[viewerIdx];
    // Rotate so viewer's physical position becomes 0 (bottom)
    return (physicalPos - viewerPhysicalPos + 4) % 4;
  }

  /**
   * Initialize cards and create containers based on game state.
   * Creates all position containers: stack, hand, general, defenders, stash per player.
   * Call after init() and before first sync().
   */
  createLayout(numPlayers, playerID) {
    if (this.cardsInitialized) return;

    // Ensure valid player count
    const safeNumPlayers = Math.min(Math.max(parseInt(numPlayers, 10) || 2, 2), 4);
    const playerIndex = parseInt(playerID, 10) || 0;
    this._currentPlayerIdx = playerIndex; // Store for fan direction calculations

    // Get table dimensions for positioning
    const $table = $(this.tableSelector);
    const tableWidth = $table.width() || 600;
    const tableHeight = $table.height() || 400;

    // Create containers for each player
    for (let i = 0; i < safeNumPlayers; i++) {
      const positions = this._getPlayerPositions(i, safeNumPlayers, tableWidth, tableHeight, playerID);
      const isCurrentPlayer = String(i) === playerID;

      // Determine rotation based on table position (clockwise: 0=bottom, 1=left, 2=top, 3=right)
      const tablePos = this._getViewPosition(i, playerIndex, safeNumPlayers);
      let rotation = 0;
      let defenderRotation = 90;  // Perpendicular to general
      if (tablePos === 1) {
        rotation = 90;            // left player: rotate CW
        defenderRotation = 0;
      } else if (tablePos === 2) {
        rotation = 180;           // top player: upside down
        defenderRotation = -90;
      } else if (tablePos === 3) {
        rotation = -90;           // right player: rotate CCW
        defenderRotation = 180;
      }

      // Stack - face-down draw pile (rotation passed directly to constructor)
      this.containers[`stack_${i}`] = new cards.Deck({
        x: positions.stack.x,
        y: positions.stack.y,
        faceUp: false,
        rotation: rotation,
      });

      // Hand - face-up only for owner (rotation passed directly to constructor)
      this.containers[`hand_${i}`] = new cards.Hand({
        x: positions.hand.x,
        y: positions.hand.y,
        faceUp: isCurrentPlayer,
        rotation: rotation,
      });

      // Hand parking area - cards move here during reveal animation
      this.containers[`handParking_${i}`] = new cards.Hand({
        x: positions.handParking.x,
        y: positions.handParking.y,
        faceUp: isCurrentPlayer,
        rotation: rotation,
      });

      // General - always face-up (rotation passed directly to constructor)
      this.containers[`general_${i}`] = new cards.Deck({
        x: positions.general.x,
        y: positions.general.y,
        faceUp: true,
        rotation: rotation,
      });

      // Defender 1 - perpendicular to general (rotation passed directly to constructor)
      this.containers[`defender1_${i}`] = new cards.Deck({
        x: positions.defender1.x,
        y: positions.defender1.y,
        faceUp: true,
        rotation: defenderRotation,
      });

      // Defender 2 - perpendicular to general (rotation passed directly to constructor)
      this.containers[`defender2_${i}`] = new cards.Deck({
        x: positions.defender2.x,
        y: positions.defender2.y,
        faceUp: true,
        rotation: defenderRotation,
      });

      // Stash - always face-down (rotation passed directly to constructor)
      this.containers[`stash_${i}`] = new cards.Deck({
        x: positions.stash.x,
        y: positions.stash.y,
        faceUp: false,
        rotation: rotation,
      });

      // Create placeholders for empty defender/stash slots
      // (setRotation automatically updates placeholder rotation)
      this.containers[`defender1_${i}`].createPlaceholder(this.tableSelector);
      this.containers[`defender2_${i}`].createPlaceholder(this.tableSelector);
      this.containers[`stash_${i}`].createPlaceholder(this.tableSelector);
      this.containers[`defender1_${i}`].setRotation(defenderRotation);
      this.containers[`defender2_${i}`].setRotation(defenderRotation);
      this.containers[`stash_${i}`].setRotation(rotation);
    }

    // Create central stack container in the center of the table
    const centerX = tableWidth / 2;
    const centerY = tableHeight / 2;

    this.containers.centralStack = new cards.Deck({
      x: centerX,
      y: centerY,
      faceUp: false,
      rotation: 0,
    });

    // Create reveal area container for attack reveal animations
    // Positioned slightly above center for visibility
    this.containers.revealArea = new cards.Deck({
      x: centerX,
      y: centerY - 30,
      faceUp: true,
      rotation: 0,
    });

    // Add all cards to central stack initially
    // They will be distributed by sync() based on G state
    this.containers.centralStack.addCards(cards.all);
    this.containers.centralStack.render({ immediate: true });

    this.cardsInitialized = true;
  }

  /**
   * Calculate all container positions for a player.
   * Returns { stack, hand, general, defenders, stash } with x, y coordinates.
   */
  _getPlayerPositions(playerIdx, numPlayers, tableWidth, tableHeight, currentPlayerID) {
    const padding = 70;
    const cardWidth = 71;
    const cardHeight = 96;

    // Get table position using physical seat assignments (clockwise: 0=bottom, 1=left, 2=top, 3=right)
    const playerIndex = parseInt(currentPlayerID, 10) || 0;
    const tablePos = this._getViewPosition(playerIdx, playerIndex, numPlayers);

    // Base edge positions (center of each edge)
    // Array indexed as: 0=bottom, 1=top, 2=left, 3=right
    const edgePositions = [
      { x: tableWidth / 2, y: tableHeight - padding, dir: 'bottom' },
      { x: tableWidth / 2, y: padding, dir: 'top' },
      { x: padding + 50, y: tableHeight / 2, dir: 'left' },
      { x: tableWidth - padding - 50, y: tableHeight / 2, dir: 'right' },
    ];

    // Map clockwise tablePos to edgePositions index
    const edgeIndex = CLOCKWISE_TO_EDGE[tablePos];
    const edge = edgePositions[edgeIndex];
    const isVertical = tablePos === 1 || tablePos === 3;  // left or right
    const isBottom = tablePos === 0;
    const isTop = tablePos === 2;
    const isLeft = tablePos === 1;

    // Offset definitions (relative to edge center)
    // For horizontal (bottom/top): x moves left/right, y moves toward/away from center
    // For vertical (left/right): swap x/y
    const stackOffset = -150;       
    const stashOffset = -230;       
    const generalOffset = 0;          
    const defender1OffsetX = -50;    
    const defender2OffsetX = 50;   
    const defendersOffsetY = 85;  
    const handOffsetX = 0;         
    const handOffsetY = 200;         

    let positions;

    if (isVertical) {
      // Left/Right players - positioned along vertical edge
      // X moves toward/away from table center, Y moves along the edge
      const xSign = isLeft ? 1 : -1; // Push containers toward table center
      const ySign = isLeft ? 1 : -1; // Left: "left" is up (-Y), Right: "left" is down (+Y)

      positions = {
        stack: { x: edge.x, y: edge.y + stackOffset * ySign },
        stash: { x: edge.x, y: edge.y + stashOffset * ySign },
        general: { x: edge.x, y: edge.y },
        defender1: { x: edge.x + defendersOffsetY * xSign, y: edge.y + defender1OffsetX * ySign },
        defender2: { x: edge.x + defendersOffsetY * xSign, y: edge.y + defender2OffsetX * ySign },
        hand: { x: edge.x + handOffsetY * xSign, y: edge.y },
        handParking: { x: edge.x, y: edge.y - stackOffset * ySign },
      };
    } else {
      // Bottom/Top players - normal x/y offsets
      const ySign = isBottom ? -1 : 1; // Flip y direction for top player
      const xSign = isTop ? -1 : 1;    // Mirror x for top player

      positions = {
        stack: { x: edge.x + stackOffset * xSign, y: edge.y },
        stash: { x: edge.x + stashOffset * xSign, y: edge.y },
        general: { x: edge.x, y: edge.y },
        defender1: { x: edge.x + defender1OffsetX * xSign, y: edge.y + defendersOffsetY * ySign },
        defender2: { x: edge.x + defender2OffsetX * xSign, y: edge.y + defendersOffsetY * ySign },
        hand: { x: edge.x, y: edge.y + handOffsetY * ySign },
        handParking: { x: edge.x - stackOffset * xSign, y: edge.y },
      };
    }

    return positions;
  }

  /**
   * Get fan direction vector for a player (toward table center).
   * @param {string} playerId - The player ID
   * @returns {{ dx: number, dy: number }}
   */
  _getFanDirection(playerId) {
    const numPlayers = Object.keys(this.containers).filter(k => k.startsWith('hand_')).length;
    const playerIdx = parseInt(playerId, 10);
    const tablePos = this._getViewPosition(playerIdx, this._currentPlayerIdx, numPlayers);

    // tablePos: 0=bottom, 1=left, 2=top, 3=right
    switch (tablePos) {
      case 0: return { dx: 0, dy: -1 }; // bottom: fan up
      case 1: return { dx: 1, dy: 0 };  // left: fan right
      case 2: return { dx: 0, dy: 1 };  // top: fan down
      case 3: return { dx: -1, dy: 0 }; // right: fan left
      default: return { dx: 0, dy: -1 };
    }
  }

  /**
   * Render a defender or general container with attack cards fanned toward table center.
   * Base card stays at container position, attacks fan outward.
   * @param {string} containerName - Container to render
   * @param {number} speed - Animation speed in ms
   * @param {boolean} collapsed - If true, stack cards instead of fanning (for defeat animation)
   */
  _renderCombatContainer(containerName, speed = 200, collapsed = false) {
    const container = this.containers[containerName];
    const combatInfo = this.combatInfo[containerName];
    const rotation = container.rotation || 0;

    if (!container || !combatInfo) return;

    const cardWidth = 71;
    const cardHeight = 96;
    // When collapsed, cards stack on top of each other (no spacing)
    const fanSpacing = collapsed ? 0 : 25;

    // Determine fan direction based on player position
    // Extract player ID from container name (e.g., "defender1_2" → "2")
    const playerId = containerName.split('_').pop();
    const fanDir = this._getFanDirection(playerId);

    // Find base card and attack cards
    const baseCardObj = this._findCard(combatInfo.baseCard);
    const attackCardObjs = combatInfo.attacks.map(id => this._findCard(id)).filter(Boolean);

    // Use explicit z-indices to ensure correct stacking order:
    // Base card at bottom, each successive attacker on top
    // Use z-indices between 51-99 to stay:
    //   - Above non-highlighted cards (z-index: 50)
    //   - Below the dark overlay (z-index: 100)
    // Each container gets its own z-index range to avoid conflicts
    const containerIndex = Object.keys(this.combatInfo).indexOf(containerName);
    const baseZ = 51 + containerIndex * 5; // e.g., 51, 56, 61, etc.

    // Render base card at container position (bottom of stack)
    if (baseCardObj) {
      const baseTop = container.y - cardHeight / 2;
      const baseLeft = container.x - cardWidth / 2;

      $(baseCardObj.el).animate({ top: baseTop, left: baseLeft }, speed);
      if (container.faceUp) baseCardObj.showCard();
      baseCardObj.rotate(rotation);
      // Use setProperty with 'important' to override CSS !important rules
      baseCardObj.el[0].style.setProperty('z-index', baseZ, 'important');
    }

    // Render attack cards fanned outward (each successive card on top)
    for (let i = 0; i < attackCardObjs.length; i++) {
      const card = attackCardObjs[i];
      const offset = (i + 1) * fanSpacing;

      const targetTop = container.y - cardHeight / 2 + fanDir.dy * offset;
      const targetLeft = container.x - cardWidth / 2 + fanDir.dx * offset;

      $(card.el).animate({ top: targetTop, left: targetLeft }, speed);
      if (container.faceUp) card.showCard();
      card.rotate(rotation);
      // Use setProperty with 'important' to override CSS !important rules
      card.el[0].style.setProperty('z-index', baseZ + 1 + i, 'important');
    }
  }

  /**
   * Sync game state to cards.js visuals.
   * Uses a target-map approach: determine where each card should go,
   * then move all cards in one pass. This avoids timing issues with
   * the remove-then-add approach.
   */
  sync(G) {
    if (!this.cardsInitialized) return;

    // Build target map: cardId → containerName
    const targetMap = {};

    // Map central stack cards
    for (const cardId of (G.centralStack || [])) {
      targetMap[cardId] = 'centralStack';
    }

    // During redistribution, show collected cards in central stack
    if (G.redistributionState?.collectedCards) {
      for (const cardId of G.redistributionState.collectedCards) {
        targetMap[cardId] = 'centralStack';
      }
    }

    // Handle reveal animation state - map attack card based on phase
    if (G.revealState) {
      const { attackCard, targetPlayerId, targetSlot, phase } = G.revealState;

      if (phase === 'revealing') {
        // Card is at center reveal area
        targetMap[attackCard] = 'revealArea';
      }
      // Note: For 'fanning', 'collapsing', 'pausing' phases, the card is already
      // in target.attacks (added by advanceRevealPhase), so it will be mapped
      // to the target container (defender or general) by the normal mapping logic below.
    }

    // Map all cards from the new G.players structure
    for (const [playerId, player] of Object.entries(G.players || {})) {
      // Stack cards
      for (const cardId of (player.stack || [])) {
        targetMap[cardId] = `stack_${playerId}`;
      }

      // Hand cards
      for (const cardId of (player.hand || [])) {
        targetMap[cardId] = `hand_${playerId}`;
      }

      // General card (now an object with { card, attacks })
      if (player.general) {
        targetMap[player.general.card] = `general_${playerId}`;
        // Map attack cards to general container as well
        for (const attackCard of (player.general.attacks || [])) {
          targetMap[attackCard] = `general_${playerId}`;
        }
      }

      // Defender cards (now objects with { card, attacks } or null)
      if (player.defender1) {
        targetMap[player.defender1.card] = `defender1_${playerId}`;
        // Map attack cards to defender container
        for (const attackCard of (player.defender1.attacks || [])) {
          targetMap[attackCard] = `defender1_${playerId}`;
        }
      }
      if (player.defender2) {
        targetMap[player.defender2.card] = `defender2_${playerId}`;
        // Map attack cards to defender container
        for (const attackCard of (player.defender2.attacks || [])) {
          targetMap[attackCard] = `defender2_${playerId}`;
        }
      }

      // Stash card
      if (player.stash) {
        targetMap[player.stash] = `stash_${playerId}`;
      }
    }

    // During reveal, park remaining hand cards for the attacker
    if (G.revealState && G.setupPhase === 'revealing') {
      const { attackerId } = G.revealState;
      const player = G.players[attackerId];

      for (const cardId of (player.hand || [])) {
        targetMap[cardId] = `handParking_${attackerId}`;
      }
    }

    // Build combat info for defenders and generals (containers with attacks)
    this.combatInfo = {};
    for (const [playerId, player] of Object.entries(G.players || {})) {
      // General combat info
      if (player.general && player.general.attacks?.length > 0) {
        this.combatInfo[`general_${playerId}`] = {
          baseCard: player.general.card,
          attacks: player.general.attacks,
        };
      }
      // Defender combat info
      if (player.defender1 && player.defender1.attacks?.length > 0) {
        this.combatInfo[`defender1_${playerId}`] = {
          baseCard: player.defender1.card,
          attacks: player.defender1.attacks,
        };
      }
      if (player.defender2 && player.defender2.attacks?.length > 0) {
        this.combatInfo[`defender2_${playerId}`] = {
          baseCard: player.defender2.card,
          attacks: player.defender2.attacks,
        };
      }
    }

    // Move each card to its target container
    for (const card of cards.all) {
      const targetName = targetMap[card.shortName];
      const targetContainer = this.containers[targetName];

      if (targetContainer && card.container !== targetContainer) {
        // addCard handles removal from old container
        targetContainer.addCard(card);
      }
    }

    // Reorder hand containers to match game state order
    // (cards.all iteration order may differ from G.players[].hand order)
    for (const [playerId, player] of Object.entries(G.players || {})) {
      const handContainer = this.containers[`hand_${playerId}`];
      if (!handContainer || !player.hand) continue;

      // Collect cards in game state order
      const handCards = [];
      for (const cardId of player.hand) {
        const card = this._findCard(cardId);
        if (card && card.container === handContainer) {
          handCards.push(card);
        }
      }

      // Rebuild container array in correct order
      if (handCards.length > 0) {
        handContainer.length = 0;
        for (const card of handCards) {
          handContainer.push(card);
          card.container = handContainer;
        }
      }
    }

    // Determine if any combat container should be rendered collapsed (defeat animation)
    const isCollapsingPhase = G.revealState?.phase === 'collapsing' || G.revealState?.phase === 'pausing';
    const collapsingTarget = isCollapsingPhase
      ? (G.revealState.targetSlot === 'general'
          ? `general_${G.revealState.targetPlayerId}`
          : `defender${G.revealState.targetSlot}_${G.revealState.targetPlayerId}`)
      : null;

    // Render all containers (rotation is now handled natively by cards.js)
    for (const [name, container] of Object.entries(this.containers)) {
      // Check if this is a combat container with attacks (custom fanned layout)
      if (this.combatInfo[name]) {
        // Collapse cards for the defeated target during collapsing/pausing phases
        const shouldCollapse = name === collapsingTarget;
        // Use slower animation for collapse to make it visible
        const speed = shouldCollapse ? 500 : 200;
        this._renderCombatContainer(name, speed, shouldCollapse);
      } else {
        // Standard render - cards.js handles rotation automatically
        container.render({ speed: 200 });
        // Ensure correct z-index for hand cards (rightmost = highest)
        if (name.startsWith('hand_')) {
          const handContainer = container;
          setTimeout(() => {
            const baseZ = 200;
            for (let i = 0; i < handContainer.length; i++) {
              handContainer[i].el[0].style.setProperty('z-index', baseZ + i, 'important');
            }
          }, 50);
        }
      }
    }

    // Update placeholder visibility based on container contents
    for (const container of Object.values(this.containers)) {
      if (container.updatePlaceholder) {
        container.updatePlaceholder();
      }
    }

    // Apply/remove reveal highlight to attack card
    if (G.revealState) {
      const attackCard = this._findCard(G.revealState.attackCard);
      if (attackCard) {
        if (G.revealState.phase === 'revealing') {
          attackCard.el.addClass('reveal-highlight');
        } else {
          attackCard.el.removeClass('reveal-highlight');
        }
      }
    }

  }

  /**
   * Find a card object by its shortName.
   */
  _findCard(shortName) {
    return cards.all.find((c) => c.shortName === shortName);
  }

  /**
   * Register click handler for a container.
   */
  registerClickHandler(containerName, callback) {
    const container = this.containers[containerName];
    if (container) {
      container.click((card) => {
        // card may be null if placeholder was clicked
        callback(card ? card.shortName : null);
      });
    }
  }

  /**
   * Apply highlighting to containers and cards.
   * @param {Object} highlights - { containers: string[], selected: string|null }
   */
  applyHighlights(highlights) {
    // Clear all highlights first
    this.clearHighlights();

    // Highlight specified containers
    for (const containerName of highlights.containers) {
      const container = this.containers[containerName];
      if (container) {
        container.highlight();

        // If this is a combat container, update z-indices to be above overlay
        if (this.combatInfo[containerName]) {
          this._updateCombatZIndex(containerName, true);
        }
      }
    }

    // Apply selected class to specific card
    if (highlights.selected) {
      const card = this._findCard(highlights.selected);
      if (card) {
        card.select();
      }
    }
  }

  /**
   * Update z-indices for combat container cards.
   * @param {string} containerName - The container name
   * @param {boolean} aboveOverlay - If true, use z-index 200+; if false, use 51+
   */
  _updateCombatZIndex(containerName, aboveOverlay) {
    const combatInfo = this.combatInfo[containerName];
    if (!combatInfo) return;

    const containerIndex = Object.keys(this.combatInfo).indexOf(containerName);
    const baseZ = aboveOverlay
      ? 200 + containerIndex * 10
      : 51 + containerIndex * 5;

    const baseCardObj = this._findCard(combatInfo.baseCard);
    if (baseCardObj) {
      baseCardObj.el[0].style.setProperty('z-index', baseZ, 'important');
    }

    const attackCardObjs = combatInfo.attacks.map(id => this._findCard(id)).filter(Boolean);
    for (let i = 0; i < attackCardObjs.length; i++) {
      attackCardObjs[i].el[0].style.setProperty('z-index', baseZ + 1 + i, 'important');
    }
  }

  /**
   * Clear all highlighting.
   */
  clearHighlights() {
    for (const container of Object.values(this.containers)) {
      container.unhighlight();
      for (let i = 0; i < container.length; i++) {
        container[i].unselect();
      }
    }

    // Reset combat container z-indices to below-overlay values
    for (const containerName of Object.keys(this.combatInfo)) {
      this._updateCombatZIndex(containerName, false);
    }
  }

  /**
   * Cleanup when Board unmounts.
   */
  destroy() {
    if (!this.initialized) return;

    // Remove all card elements
    $(this.tableSelector).find('.card').remove();

    this.containers = {};
    this.initialized = false;
    this.cardsInitialized = false;
  }
}

export default CardTableBridge;
